import { useCallback, useEffect, useRef, useState } from 'react';
import { zestLocalStorage } from 'helpers/storage';
import { xorBy } from 'lodash';
import {
  useGetUserQuery,
  useSaveTableSettingsMutation,
} from 'redux/apiSlices/user.slices';
import { ETableNames, TTableSettings } from './Table.types';

type TUseTableSettingsProps = {
  initialData: TTableSettings[];
  tableName: ETableNames;
};
type TTUseTableSettingsPropsReturn = [
  TTableSettings[],
  React.Dispatch<React.SetStateAction<TTableSettings[]>>,
];

const DEBOUNCE_MS = 1000;

const useTableSettings = ({
  initialData,
  tableName,
}: TUseTableSettingsProps): TTUseTableSettingsPropsReturn => {
  const { data: userData } = useGetUserQuery({});
  const [saveSettings] = useSaveTableSettingsMutation();
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isServerLoaded = useRef(false);
  const isDirty = useRef(false);
  const userIdRef = useRef<string | undefined>();
  const saveSettingsRef = useRef(saveSettings);

  // Initialize from localStorage (instant), server will override once loaded
  const [settings, setSettings] = useState<TTableSettings[]>(
    zestLocalStorage.getItem('TableSettings')?.[tableName] || initialData,
  );

  // Wrap setSettings so we can track user-initiated changes
  const setSettingsDirty: typeof setSettings = useCallback(value => {
    isDirty.current = true;
    setSettings(value);
  }, []);

  userIdRef.current = userData?.id;
  saveSettingsRef.current = saveSettings;

  // Flush pending save on unmount only
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      if (userIdRef.current && isDirty.current) {
        const latestStorage = zestLocalStorage.getItem('TableSettings');
        saveSettingsRef.current({
          resourceId: userIdRef.current,
          tableSettingsJson: JSON.stringify(latestStorage),
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Once user profile arrives, seed from server blob (overrides localStorage)
  useEffect(() => {
    if (!userData?.tableSettingsJson || isServerLoaded.current) return;
    try {
      const allSettings: Record<string, TTableSettings[]> = JSON.parse(
        userData.tableSettingsJson,
      );
      const serverEntry = allSettings[tableName];
      if (serverEntry) {
        // Merge: preserve server order, append new columns from initialData
        if (xorBy(initialData, serverEntry, 'title').length) {
          const merged = [
            ...serverEntry.filter(s =>
              initialData.some(i => i.title === s.title),
            ),
            ...initialData.filter(
              i => !serverEntry.some(s => s.title === i.title),
            ),
          ];
          setSettings(merged);
        } else {
          setSettings(serverEntry);
        }
      }
    } catch {
      // Invalid JSON from server, keep current settings
    }
    isServerLoaded.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.tableSettingsJson, tableName, initialData]);

  // Persist to localStorage immediately + debounced save to server
  useEffect(() => {
    const currStorage = zestLocalStorage.getItem('TableSettings');
    const currTableStorage: TTableSettings[] = currStorage?.[tableName];

    // Merge new/removed columns from initialData
    if (
      !!currTableStorage &&
      xorBy(initialData, currTableStorage, 'title').length
    ) {
      const merged = [
        ...currTableStorage.filter(s =>
          initialData.some(i => i.title === s.title),
        ),
        ...initialData.filter(
          i => !currTableStorage.some(s => s.title === i.title),
        ),
      ];
      setSettings(merged);
    }

    const newStorageObj = { ...currStorage, [tableName]: settings };
    zestLocalStorage.setItem('TableSettings', newStorageObj);

    // Debounced save of the full blob to server — only after user interaction
    if (userData?.id && isDirty.current) {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      debounceTimer.current = setTimeout(() => {
        const latestStorage = zestLocalStorage.getItem('TableSettings');
        saveSettings({
          resourceId: userData.id,
          tableSettingsJson: JSON.stringify(latestStorage),
        });
      }, DEBOUNCE_MS);
    }

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, initialData, tableName, userData?.id, saveSettings]);

  return [settings, setSettingsDirty];
};

export default useTableSettings;
